import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ServerCalendar = () => {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const { token, getAuthHeaders } = useAuth();
  
  const [server, setServer] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showDeleteAvailabilityModal, setShowDeleteAvailabilityModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedAvailability, setSelectedAvailability] = useState(null);

  // Get Monday of the week for a given date
  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  }

  useEffect(() => {
    if (token && serverId) {
      fetchServer();
      fetchAvailability();
      fetchAppointments();
    }
  }, [token, serverId, currentWeekStart]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Update current time every minute for the time indicator
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const fetchServer = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/servers/${serverId}`, {
        headers: getAuthHeaders()
      });
      setServer(response.data.data);
    } catch (err) {
      console.error('Error fetching server:', err);
      setError('Failed to load server information');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startDate = formatDate(currentWeekStart);
      const endDate = formatDate(weekEnd);

      const response = await axios.get(`${API_BASE_URL}/availability`, {
        params: { 
          server_id: serverId,
          start_date: startDate,
          end_date: endDate
        },
        headers: getAuthHeaders()
      });
      
      setAvailability(response.data.data || []);
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  };

  const fetchAppointments = async () => {
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startDate = formatDate(currentWeekStart);
      const endDate = formatDate(weekEnd);

      const response = await axios.get(`${API_BASE_URL}/appointments`, {
        params: { 
          server_id: serverId,
          start_date: startDate,
          end_date: endDate
        },
        headers: getAuthHeaders()
      });
      
      setAppointments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTime = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const getDaysOfWeek = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(getWeekStart(newDate));
  };

  const isAvailable = (date, hour) => {
    const dateStr = formatDate(date);
    return availability.some(avail => {
      if (avail.date !== dateStr) return false;
      
      // Parse start_time and end_time (format: "HH:MM")
      const startHour = parseInt(avail.start_time.split(':')[0]);
      const endHour = parseInt(avail.end_time.split(':')[0]);
      
      return hour >= startHour && hour < endHour;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const getAppointmentsForSlot = (date, hour) => {
    const dateStr = formatDate(date);
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    
    return appointments.filter(apt => {
      const aptDate = apt.start_time?.split('T')[0];
      const aptTime = apt.start_time?.split('T')[1]?.substring(0, 5);
      return aptDate === dateStr && aptTime === timeStr;
    });
  };

  const handleCellRightClick = (e, date, hour) => {
    e.preventDefault();
    const dateStr = formatDate(date);
    const hasAvailability = availability.some(avail => {
      if (avail.date !== dateStr) return false;
      const startHour = parseInt(avail.start_time.split(':')[0]);
      const endHour = parseInt(avail.end_time.split(':')[0]);
      return hour >= startHour && hour < endHour;
    });

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      hasAvailability
    });
    
    setSelectedSlot({
      date: dateStr,
      hour: hour,
      time: `${hour.toString().padStart(2, '0')}:00`
    });

    if (hasAvailability) {
      const availSlot = availability.find(avail => {
        if (avail.date !== dateStr) return false;
        const startHour = parseInt(avail.start_time.split(':')[0]);
        const endHour = parseInt(avail.end_time.split(':')[0]);
        return hour >= startHour && hour < endHour;
      });
      setSelectedAvailability(availSlot);
    }
  };

  const handleSetAvailability = () => {
    setContextMenu(null);
    setShowAvailabilityModal(true);
  };

  const handleDeleteAvailabilityClick = () => {
    setContextMenu(null);
    setShowDeleteAvailabilityModal(true);
  };

  const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 7 AM to 8 PM

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-gray-600">Loading server calendar...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <section className="flex-grow py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/admin/servers')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Manage Servers
          </button>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {server && (
            <>
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">
                  {server.server_name}&apos;s Calendar
                </h1>
                <p className="text-gray-600">Set working hours and availability for this server</p>
              </div>

              {/* Calendar Controls */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => navigateWeek(-1)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <div className="text-lg font-semibold text-gray-800">
                    Week of {currentWeekStart.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </div>
                  
                  <button
                    onClick={() => navigateWeek(1)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="min-w-[800px]">
                    {/* Day Headers */}
                    <div className="grid grid-cols-8 border-b bg-gray-50">
                      <div className="p-4 font-semibold text-gray-600">Time</div>
                      {getDaysOfWeek().map((day, idx) => {
                        const today = isToday(day);
                        return (
                          <div 
                            key={idx} 
                            className={`p-4 text-center border-l ${today ? 'bg-red-100' : ''}`}
                          >
                            <div className={`font-semibold ${today ? 'text-red-700' : 'text-gray-800'}`}>
                              {day.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div className={`text-sm ${today ? 'text-red-600' : 'text-gray-500'}`}>
                              {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Time Slots */}
                    {hours.map((hour) => (
                      <div key={hour} className="grid grid-cols-8 border-b hover:bg-gray-50">
                        <div className="p-4 border-r bg-gray-50 text-sm font-medium text-gray-600">
                          {formatTime(hour)}
                        </div>
                        {getDaysOfWeek().map((day, idx) => {
                          const available = isAvailable(day, hour);
                          const slotAppointments = getAppointmentsForSlot(day, hour);
                          const hasAppointment = slotAppointments.length > 0;
                          const today = isToday(day);
                          
                          // Calculate if current time indicator should appear in this slot
                          const showTimeIndicator = today && 
                            currentTime.getHours() === hour && 
                            currentTime.getMinutes() > 0;
                          const timeIndicatorPosition = today && currentTime.getHours() === hour
                            ? (currentTime.getMinutes() / 60) * 100
                            : 0;
                          
                          return (
                            <div
                              key={idx}
                              className={`relative p-2 border-l min-h-[60px] transition-colors cursor-pointer hover:opacity-90 ${
                                hasAppointment 
                                  ? 'bg-red-100' 
                                  : available 
                                    ? 'bg-green-50' 
                                    : ''
                              }`}
                              onContextMenu={(e) => handleCellRightClick(e, day, hour)}
                            >
                              {hasAppointment ? (
                                <div className="text-xs space-y-1">
                                  {slotAppointments.map((apt, aptIdx) => (
                                    <div key={aptIdx} className="bg-red-200 p-1 rounded border border-red-300">
                                      <div className="font-semibold text-red-900 truncate">
                                        👤 {apt.customer_name || 'Customer'}
                                      </div>
                                      <div className="text-red-700 truncate">
                                        ⚡ {apt.service_name || 'Service'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : available ? (
                                <div className="text-xs text-green-700 font-medium">
                                  Available
                                </div>
                              ) : null}
                              
                              {/* Current time indicator - only show in today's column */}
                              {showTimeIndicator && (
                                <div 
                                  className="absolute left-0 right-0 h-0.5 bg-blue-500 z-10"
                                  style={{ top: `${timeIndicatorPosition}%` }}
                                >
                                  <div className="absolute -left-1 -top-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <div className="absolute -right-1 -top-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-4 text-sm text-gray-600 text-center">
                Right-click on any time slot to set or remove availability
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white shadow-lg rounded-md border border-gray-200 py-2 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {contextMenu.hasAvailability ? (
            <button
              onClick={handleDeleteAvailabilityClick}
              className="w-full px-6 py-2 text-left hover:bg-red-50 transition-colors text-red-600"
            >
              <span className="font-medium">Delete Availability</span>
            </button>
          ) : (
            <button
              onClick={handleSetAvailability}
              className="w-full px-6 py-2 text-left hover:bg-blue-50 transition-colors"
            >
              <span className="font-medium">Set Availability</span>
            </button>
          )}
        </div>
      )}

      {/* Set Availability Modal */}
      {showAvailabilityModal && (
        <AvailabilityModal
          serverId={serverId}
          selectedSlot={selectedSlot}
          onClose={() => {
            setShowAvailabilityModal(false);
            setSelectedSlot(null);
            fetchAvailability();
            fetchAppointments();
          }}
          token={token}
        />
      )}

      {/* Delete Availability Modal */}
      {showDeleteAvailabilityModal && (
        <DeleteAvailabilityModal
          serverId={serverId}
          availability={selectedAvailability}
          selectedSlot={selectedSlot}
          onClose={() => {
            setShowDeleteAvailabilityModal(false);
            setSelectedAvailability(null);
            fetchAvailability();
            fetchAppointments();
          }}
          token={token}
        />
      )}
    </div>
  );
};

// Availability Modal Component
const AvailabilityModal = ({ serverId, selectedSlot, onClose, token }) => {
  const defaultStartTime = selectedSlot?.time || '09:00';
  const defaultEndTime = calculateEndTime(defaultStartTime, 1);
  
  const [formData, setFormData] = useState({
    start_time: defaultStartTime,
    end_time: defaultEndTime,
    recurrence_pattern: 'once',
    end_date: '',
    full_office_hours: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function calculateEndTime(startTime, hoursToAdd) {
    const [hours, minutes] = startTime.split(':').map(Number);
    const newHour = (hours + hoursToAdd) % 24;
    return `${newHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Update end time when start time changes
  const handleStartTimeChange = (newStartTime) => {
    setFormData(prev => ({
      ...prev,
      start_time: newStartTime,
      end_time: prev.full_office_hours ? prev.end_time : calculateEndTime(newStartTime, 1)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const availabilityData = {
        server_id: serverId,
        date: selectedSlot.date,
        start_time: formData.full_office_hours ? '07:00' : formData.start_time,
        end_time: formData.full_office_hours ? '21:00' : formData.end_time,
        recurrence_type: formData.recurrence_pattern === 'once' ? 'none' : formData.recurrence_pattern,
        recurrence_months: formData.end_date ? undefined : 1,
        is_available: true
      };

      await axios.post(
        `${API_BASE_URL}/availability`,
        availabilityData,
        { headers: getAuthHeaders() }
      );

      onClose();
    } catch (err) {
      console.error('Error creating availability:', err);
      const errorDetail = err.response?.data?.detail;
      
      // Handle validation errors (array of error objects)
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
        setError(errorMessages);
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (typeof errorDetail === 'object') {
        setError(JSON.stringify(errorDetail));
      } else {
        setError('Failed to set availability');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Set Server Availability</h2>
          <p className="text-gray-600 mt-1">
            Date: {new Date(selectedSlot.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.full_office_hours}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  full_office_hours: e.target.checked,
                  start_time: e.target.checked ? '07:00' : formData.start_time,
                  end_time: e.target.checked ? '21:00' : formData.end_time
                })}
                className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Full Office Hours (7 AM - 9 PM)
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <select
                value={formData.start_time}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {Array.from({ length: 14 }, (_, i) => i + 7).map(hour => (
                  <option key={hour} value={`${hour.toString().padStart(2, '0')}:00`}>
                    {hour === 0 ? '12' : hour > 12 ? hour - 12 : hour}:00 {hour >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <select
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {Array.from({ length: 14 }, (_, i) => i + 7).map(hour => (
                  <option key={hour} value={`${hour.toString().padStart(2, '0')}:00`}>
                    {hour === 0 ? '12' : hour > 12 ? hour - 12 : hour}:00 {hour >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recurrence Pattern
            </label>
            <select
              value={formData.recurrence_pattern}
              onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="once">Once (single day)</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {(formData.recurrence_pattern === 'daily' || formData.recurrence_pattern === 'weekly') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date (optional)
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                min={selectedSlot.date}
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md disabled:bg-yellow-300"
            >
              {loading ? 'Setting...' : 'Set Availability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Availability Modal Component
const DeleteAvailabilityModal = ({ serverId, availability, selectedSlot, onClose, token }) => {
  function calculateDefaultEndTime(startTime) {
    const [hours, minutes] = startTime.split(':').map(Number);
    const newHour = (hours + 1) % 24;
    return `${newHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const startTime = selectedSlot?.time || '09:00';
  const [formData, setFormData] = useState({
    start_time: startTime,
    end_time: calculateDefaultEndTime(startTime),
    recurrence_pattern: 'once',
    recurrence_months: 1,
    full_office_hours: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleStartTimeChange = (newStartTime) => {
    setFormData({
      ...formData,
      start_time: newStartTime,
      end_time: formData.full_office_hours ? formData.end_time : calculateDefaultEndTime(newStartTime)
    });
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate that we have an availability record
      if (!availability || !availability.id) {
        setError('Unable to identify the availability slot to delete. Please try again.');
        setLoading(false);
        return;
      }

      const requestData = {
        date: selectedSlot.date,
        start_time: formData.full_office_hours ? '07:00' : formData.start_time,
        end_time: formData.full_office_hours ? '21:00' : formData.end_time,
        recurrence_type: formData.recurrence_pattern === 'once' ? 'none' : formData.recurrence_pattern
      };

      // Add duration for recurring deletions
      if (formData.recurrence_pattern !== 'once' && formData.recurrence_months) {
        requestData.recurrence_months = parseInt(formData.recurrence_months);
      }

      await axios.delete(
        `${API_BASE_URL}/availability/${availability.id}`,
        { 
          headers: getAuthHeaders(),
          data: requestData
        }
      );

      onClose();
    } catch (err) {
      console.error('Error deleting availability:', err);
      const errorDetail = err.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
        setError(errorMessages);
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Failed to delete availability');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-red-600">Delete Server Availability</h2>
            <p className="text-gray-600 mt-1">
              Starting from {new Date(selectedSlot?.date).toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleDelete} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.full_office_hours}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  full_office_hours: e.target.checked,
                  start_time: e.target.checked ? '07:00' : formData.start_time,
                  end_time: e.target.checked ? '21:00' : formData.end_time
                })}
                className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Full Office Hours (7 AM - 9 PM)
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <select
                value={formData.start_time}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {Array.from({ length: 14 }, (_, i) => i + 7).map(hour => (
                  <option key={hour} value={`${hour.toString().padStart(2, '0')}:00`}>
                    {hour === 0 ? '12' : hour > 12 ? hour - 12 : hour}:00 {hour >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <select
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {Array.from({ length: 14 }, (_, i) => i + 7).map(hour => (
                  <option key={hour} value={`${hour.toString().padStart(2, '0')}:00`}>
                    {hour === 0 ? '12' : hour > 12 ? hour - 12 : hour}:00 {hour >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recurrence Pattern
            </label>
            <select
              value={formData.recurrence_pattern}
              onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="once">One-time only</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {formData.recurrence_pattern !== 'once' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (months)
              </label>
              <input
                type="number"
                value={formData.recurrence_months}
                onChange={(e) => setFormData({ ...formData, recurrence_months: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                min="1"
                max="12"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.recurrence_pattern === 'daily' 
                  ? 'Default: 1 month from start date' 
                  : 'Default: 3 months from start date'}
              </p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2 text-sm">Warning:</h4>
            <ul className="list-disc list-inside space-y-1 text-xs text-yellow-700">
              <li>This will permanently remove the selected server availability</li>
              <li>Server will show as unavailable for this time period</li>
              {formData.recurrence_pattern !== 'once' && (
                <li className="font-semibold">All instances within the duration will be deleted</li>
              )}
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md disabled:bg-red-300"
            >
              {loading ? 'Deleting...' : 'Delete Availability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServerCalendar;
